import { supabase } from "@/integrations/supabase/client";
import { PageView, DailyPageView } from "@/types/analytics";
import { startOfWeek, endOfWeek, format, eachDayOfInterval } from "date-fns";

// Format currency based on the provided currency code
export function formatCurrency(amount: number, currency?: string | null): string {
  // If amount is not a number, return 0 formatted
  if (isNaN(amount)) amount = 0;
  
  // Default to KES if currency is undefined, null, or empty string
  const currencyCode = currency && currency.trim() !== '' ? currency : 'KES';
  
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  } catch (error) {
    // If there's an error with the currency format, fall back to a simple format
    console.error(`Error formatting currency ${currencyCode}:`, error);
    return `${currencyCode} ${amount}`;
  }
}

// One single function to increment page views - this will update both total and daily counts
export const incrementPageViews = async (userId: string): Promise<void> => {
  if (!userId) {
    console.error("Cannot increment page views without a userId");
    return;
  }

  const MAX_RETRIES = 3;
  let retries = 0;
  let success = false;

  while (retries < MAX_RETRIES && !success) {
    try {
      console.log(`Incrementing page views for user: ${userId} (attempt ${retries + 1})`);
      
      // Call the edge function to increment both total and daily views
      const { data, error } = await supabase.functions.invoke('increment-page-views', {
        body: { userId }
      });

      if (error) {
        console.error(`Error incrementing page views (attempt ${retries + 1}):`, error);
        retries++;
        
        // Add a small delay before retrying
        if (retries < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, 1000 * retries));
        }
      } else {
        success = true;
        console.log("Successfully incremented page views");
      }
    } catch (error) {
      console.error(`Error invoking increment-page-views function (attempt ${retries + 1}):`, error);
      retries++;
      
      // Add a small delay before retrying
      if (retries < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, 1000 * retries));
      }
    }
  }

  if (!success) {
    console.error(`Failed to increment page views after ${MAX_RETRIES} attempts`);
  }
};

// This is just a wrapper around incrementPageViews for backward compatibility
export const recordDailyPageView = async (userId: string): Promise<void> => {
  await incrementPageViews(userId);
};

// Get the total views for the current week (Sunday to Saturday)
export const getWeeklyPageViews = async (userId: string): Promise<number> => {
  try {
    // Get start and end of the current week (Sunday to Saturday)
    const now = new Date();
    const startDate = startOfWeek(now, { weekStartsOn: 0 });  // 0 = Sunday
    const endDate = endOfWeek(now, { weekStartsOn: 0 });      // End of Saturday
    
    // Format dates for database query
    const formattedStartDate = format(startDate, 'yyyy-MM-dd');
    const formattedEndDate = format(endDate, 'yyyy-MM-dd');
    
    console.log(`Fetching weekly views from ${formattedStartDate} to ${formattedEndDate}`);
    
    // Query the database for daily views in the date range
    const { data: weeklyViews, error } = await supabase
      .from('daily_page_views')
      .select('view_count, view_date')
      .eq('user_id', userId)
      .gte('view_date', formattedStartDate)
      .lte('view_date', formattedEndDate);
    
    if (error) {
      console.error('Error fetching weekly page views:', error);
      return 0;
    }
    
    // Sum up the view counts for the week
    const totalWeeklyViews = (weeklyViews || []).reduce((sum, item) => sum + item.view_count, 0);
    console.log('Total weekly views:', totalWeeklyViews);
    
    return totalWeeklyViews;
  } catch (error) {
    console.error('Error calculating weekly views:', error);
    return 0;
  }
};

// Get total page views directly from the page_views table
export const getTotalPageViews = async (userId: string): Promise<number> => {
  try {
    const { data, error } = await supabase
      .from('page_views')
      .select('view_count')
      .eq('user_id', userId)
      .single();
    
    if (error) {
      console.error('Error fetching total page views:', error);
      return 0;
    }

    return data?.view_count || 0;
  } catch (error) {
    console.error('Error getting total page views:', error);
    return 0;
  }
};

// Get the page view count for a specific day
export const getPageViewsForDate = async (userId: string, date: string): Promise<number> => {
  try {
    const { data, error } = await supabase
      .from('daily_page_views')
      .select('view_count')
      .eq('user_id', userId)
      .eq('view_date', date)
      .single();
    
    if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
      console.error('Error fetching page views for date:', error);
      return 0;
    }

    return data?.view_count || 0;
  } catch (error) {
    console.error('Error getting page views for date:', error);
    return 0;
  }
};

// Function to update the user's last_update timestamp
export const updateUserLastActivity = async (userId: string): Promise<void> => {
  if (!userId) return;
  
  try {
    // Update the user's last_update timestamp
    const { error } = await supabase
      .from('profiles')
      .update({ last_update: new Date().toISOString() })
      .eq('id', userId);
    
    if (error) {
      console.error('Error updating last activity timestamp:', error);
    }
  } catch (error) {
    console.error('Exception updating last activity timestamp:', error);
  }
};

// Disabled user action logging functionality
export const logUserAction = async (
  actionType: string,
  component?: string | null,
  pagePath?: string | null,
  actionDetails?: Record<string, any>,
  sessionId?: string,
  userId?: string
): Promise<void> => {
  // Check if this is a page view action, and if so, increment the page views
  if (actionType === 'page_view') {
    try {
      // Get the current user if userId is not provided
      if (!userId) {
        const { data } = await supabase.auth.getUser();
        userId = data.user?.id;
      }
      
      // If we have a userId, update last activity for all pages
      if (userId) {
        // Only increment page views for service pages (/services/)
        if (pagePath && pagePath.includes('/services/')) {
          await incrementPageViews(userId);
          console.log(`Page view tracked for user: ${userId}, page: ${pagePath || 'unknown'}`);
        }
        
        // Always update last activity timestamp regardless of page
        await updateUserLastActivity(userId);
      }
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  } else {
    // For non-page-view actions, still update the last activity timestamp
    try {
      // Get the current user if userId is not provided
      if (!userId) {
        const { data } = await supabase.auth.getUser();
        userId = data.user?.id;
      }
      
      // If we have a userId, update last activity
      if (userId) {
        await updateUserLastActivity(userId);
      }
    } catch (error) {
      console.error('Error updating last activity for action:', error);
    }
  }
  
  // For other action types, we could implement additional tracking in the future
  console.log(`User action tracked: ${actionType}`, {
    component,
    pagePath,
    ...actionDetails
  });
};

// Add user login logging functionality
export const logUserLogin = async (userId: string, ipAddress?: string, deviceInfo?: string): Promise<void> => {
  try {
    const { error } = await supabase.functions.invoke('log-user-login', {
      body: { 
        userId, 
        ipAddress, 
        deviceInfo 
      }
    });

    if (error) {
      console.error('Error logging user login:', error);
    }
  } catch (error) {
    console.error('Error invoking log-user-login function:', error);
  }
};
